/* eslint-disable react/jsx-props-no-spreading */
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import InputMask from 'react-input-mask';
import { object, string } from 'yup';
import cx from 'classnames';
import { ERRORS } from 'appConstants';
import { meSendApplicationsAction } from 'store/me/actions';
import { RequestStatus, SendApplicationsReq, State } from 'types';
import {
  _t,
  formatApplicationSourceLine,
  lockBodyScroll,
  sendMetrics,
} from 'utils';
import type { ApplicationFormSource } from 'utils';

import iconSend from 'assets/img/form-v4/icon-send.svg';

import styles from './styles.module.scss';

type Props = {
  onClose: () => void;
  source?: ApplicationFormSource | null;
};

type FormValues = SendApplicationsReq & {
  inn: string;
  sphere: string;
};

const SPHERE_OPTIONS = [
  { value: '', label: ' - Выберите -' },
  { value: 'industry', label: 'Промышленность' },
  { value: 'agro', label: 'АПК' },
  { value: 'it', label: 'IT / цифровые технологии' },
  { value: 'trade', label: 'Торговля' },
  { value: 'other', label: 'Другое' },
];

const initialValues: FormValues = {
  nameOfCompany: '',
  inn: '',
  sphere: '',
  fullName: '',
  position: '',
  phone: '',
  email: '',
  text: '',
};

const validationSchema = object().shape({
  nameOfCompany: string().required(ERRORS.required),
  inn: string().required(ERRORS.required),
  fullName: string().required(ERRORS.required),
  phone: string().min(18, ERRORS.phone).required(ERRORS.required),
  email: string().email(ERRORS.email).required(ERRORS.required),
  position: string().required(ERRORS.required),
  text: string().required(ERRORS.required),
});

const ApplicationForm: FC<Props> = ({ onClose, source }) => {
  const dispatch = useDispatch();
  const sendStatus = useSelector(({ ui }: State) => ui.ME_SEND_APPLICATIONS);
  const isSending = sendStatus === RequestStatus.REQUEST;
  const hasSubmittedRef = useRef(false);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    resetForm,
    setErrors,
  } = useFormik<FormValues>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: (form) => {
      const sphereLabel = SPHERE_OPTIONS.find((o) => o.value === form.sphere)?.label;
      const meta = [
        form.inn && `ИНН: ${form.inn}`,
        form.sphere && `Сфера: ${sphereLabel || form.sphere}`,
        formatApplicationSourceLine(source),
      ].filter(Boolean).join('\n');

      hasSubmittedRef.current = true;
      dispatch(meSendApplicationsAction({
        nameOfCompany: form.nameOfCompany,
        fullName: form.fullName,
        position: form.position,
        phone: form.phone,
        email: form.email,
        text: [meta, form.text].filter(Boolean).join('\n\n'),
      }));
      sendMetrics('', ['form_make_application', 'form']);
    },
  });

  useEffect(() => lockBodyScroll(), []);

  useEffect(() => {
    if (!hasSubmittedRef.current) return;

    if (sendStatus === RequestStatus.SUCCESS) {
      hasSubmittedRef.current = false;
      resetForm();
      onClose();
    }

    if (sendStatus === RequestStatus.ERROR) {
      hasSubmittedRef.current = false;
      setErrors({
        nameOfCompany: '',
        inn: '',
        fullName: '',
        phone: '',
        email: '',
        text: '',
      });
    }
  }, [sendStatus, resetForm, onClose, setErrors]);

  const handlePhoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    handleChange({
      target: {
        name: e.target.name,
        value: e.target.value.replace(/_+$/, ''),
      },
    });
  }, [handleChange]);

  const renderLabel = (text: string, required = false, medium = false) => (
    <span className={cx(styles.label, medium && styles.labelMedium)}>
      {_t(text)}
      {required && <span className={styles.req}>*</span>}
    </span>
  );

  const fieldError = (name: keyof FormValues) => (
    errors[name] && touched[name] ? (
      <span className={styles.error}>{_t(String(errors[name]))}</span>
    ) : null
  );

  return createPortal(
    <div className={styles.root} role="dialog" aria-modal="true" aria-label={_t('Заявка')}>
      <div className={styles.backdrop} />

      <div className={styles.scroll} onClick={onClose} role="presentation">
        <div className={styles.card} onClick={(e) => e.stopPropagation()}>
          <div className={styles.head}>
            <h2 className={styles.title}>{_t('Заявка')}</h2>
            <button
              type="button"
              className={styles.close}
              aria-label={_t('Закрыть')}
              onClick={onClose}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M1 1l14 14M15 1L1 15" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
          </div>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <div className={styles.field}>
              {renderLabel('Компания', true)}
              <input
                className={styles.control}
                name="nameOfCompany"
                value={values.nameOfCompany}
                placeholder={_t('Наименование компании')}
                onChange={handleChange}
              />
              {fieldError('nameOfCompany')}
            </div>

            <div className={styles.field}>
              {renderLabel('ИНН', true)}
              <input
                className={styles.control}
                name="inn"
                value={values.inn}
                placeholder={_t('ИНН')}
                onChange={handleChange}
                inputMode="numeric"
              />
              {fieldError('inn')}
            </div>

            <div className={styles.field}>
              {renderLabel('Сфера деятельности', false, true)}
              <select
                className={cx(styles.control, styles.select, !values.sphere && styles.placeholder)}
                name="sphere"
                value={values.sphere}
                onChange={handleChange}
              >
                {SPHERE_OPTIONS.map((opt) => (
                  <option key={opt.value || 'empty'} value={opt.value}>
                    {_t(opt.label)}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              {renderLabel('ФИО', true)}
              <input
                className={styles.control}
                name="fullName"
                value={values.fullName}
                placeholder={_t('Иванов Иван Иванович')}
                onChange={handleChange}
              />
              {fieldError('fullName')}
            </div>

            <div className={styles.field}>
              {renderLabel('Должность', true)}
              <input
                className={styles.control}
                name="position"
                value={values.position}
                placeholder={_t('Роль в компании')}
                onChange={handleChange}
              />
              {fieldError('position')}
            </div>

            <div className={styles.field}>
              {renderLabel('E-mail', true)}
              <input
                className={styles.control}
                name="email"
                type="email"
                value={values.email}
                placeholder={_t('email@mail.ru')}
                onChange={handleChange}
              />
              {fieldError('email')}
            </div>

            <div className={styles.field}>
              {renderLabel('Телефон', true)}
              <InputMask
                mask="+7 (999) 999-99-99"
                value={values.phone}
                onChange={handlePhoneChange}
              >
                {(inputProps: object) => (
                  <input
                    {...inputProps}
                    className={styles.control}
                    name="phone"
                    placeholder={_t('+7 (999) 000-00-00')}
                  />
                )}
              </InputMask>
              {fieldError('phone')}
            </div>

            <div className={styles.field}>
              {renderLabel('Ваш вопрос', true)}
              <textarea
                className={styles.textarea}
                name="text"
                value={values.text}
                placeholder={_t('Напишите текст вопроса здесь')}
                onChange={handleChange}
                rows={4}
              />
              {fieldError('text')}
            </div>

            <button type="submit" className={styles.submit} disabled={isSending}>
              <span className={styles.submitLabel}>{_t('Записаться онлайн')}</span>
              <span className={styles.submitIcon} aria-hidden="true">
                <img src={iconSend} alt="" width={16} height={16} />
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ApplicationForm;
